import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { IndianRupee, TrendingUp, Clock, AlertTriangle, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const STATUS_STYLES = {
  paid: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  overdue: "bg-red-50 text-red-700",
};

const formatINR = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export default function Collections() {
  const [records, setRecords] = useState(null);

  useEffect(() => {
    api
      .get("/collections")
      .then((r) => setRecords(r.data))
      .catch((e) => {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        setRecords([]);
      });
  }, []);

  const stats = useMemo(() => {
    if (!records) return { collected: 0, pending: 0, overdue: 0 };
    return {
      collected: records.filter((r) => r.status === "paid").reduce((s, r) => s + r.amount, 0),
      pending: records.filter((r) => r.status === "pending").reduce((s, r) => s + r.amount, 0),
      overdue: records.filter((r) => r.status === "overdue").reduce((s, r) => s + r.amount, 0),
    };
  }, [records]);

  return (
    <div data-testid="collections-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Platform Admin</p>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="collections-heading">
            Collections
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Maintenance collections across every registered society — complete records, no filters applied.
          </p>
        </div>
        <span className="rounded-full bg-blue-50 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700" data-testid="collections-total-badge">
          {records ? records.length : 0} records
        </span>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="collections-stats">
        {[
          [TrendingUp, formatINR(stats.collected), "Collected", "text-emerald-600 bg-emerald-50"],
          [Clock, formatINR(stats.pending), "Pending", "text-amber-600 bg-amber-50"],
          [AlertTriangle, formatINR(stats.overdue), "Overdue", "text-red-600 bg-red-50"],
        ].map(([Icon, val, label, color]) => (
          <div key={label} className="card-soft flex items-center gap-4 p-5">
            <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}>
              <Icon size={20} />
            </span>
            <div>
              <p className="text-xl font-extrabold text-slate-900">{val}</p>
              <p className="text-xs text-slate-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="card-soft overflow-hidden" data-testid="collections-table-card">
        {records === null ? (
          <div className="flex items-center justify-center gap-3 p-16 text-slate-400" data-testid="collections-loading">
            <Loader2 size={20} className="animate-spin" /> Loading collections…
          </div>
        ) : records.length === 0 ? (
          <p className="p-16 text-center text-sm text-slate-400" data-testid="collections-empty">
            No collection records yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left" data-testid="collections-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  {["Society", "Month", "Amount", "Method", "Status", "Date"].map((h) => (
                    <th key={h} className="px-5 py-3.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} data-testid={`collection-row-${r.id}`} className="border-b border-slate-50 transition-colors hover:bg-blue-50/40">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                          <IndianRupee size={15} />
                        </span>
                        <p className="text-sm font-bold text-slate-900">{r.society_name}</p>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm font-medium text-slate-700">{r.month}</td>
                    <td className="px-5 py-4 text-sm font-extrabold text-slate-900">{formatINR(r.amount)}</td>
                    <td className="px-5 py-4">
                      <span className="rounded-full bg-slate-100 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                        {r.method}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLES[r.status] || STATUS_STYLES.pending}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-500">{formatDate(r.collected_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
